import React, { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle, XCircle, Loader2, Mail, ArrowLeft, RefreshCw } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState('loading'); // loading, success, error, expired, no-token
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  
  // For resend verification
  const [resendEmail, setResendEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendError, setResendError] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('no-token');
      return;
    }
    
    verifyEmail();
  }, [token]);

  const verifyEmail = async () => {
    setStatus('loading');
    
    try {
      const response = await axios.post(`${API}/auth/verify-email`, {
        token: token
      });
      
      if (response.data.success) {
        setStatus('success');
        setMessage(response.data.message);
        setEmail(response.data.email || '');
      }
    } catch (err) {
      const errorDetail = err.response?.data?.detail || 'Une erreur est survenue';
      
      if (errorDetail.includes('expiré')) {
        setStatus('expired');
      } else if (errorDetail.includes('utilisé')) {
        setStatus('already-used');
      } else {
        setStatus('error');
      }
      
      setMessage(errorDetail);
    }
  };

  const handleResendVerification = async (e) => {
    e.preventDefault();
    setResendError('');
    setResendLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/resend-verification`, {
        email: resendEmail
      });
      
      if (response.data.success) {
        setResendSuccess(true);
      }
    } catch (err) {
      if (err.response?.status === 429) {
        setResendError('Trop de demandes. Veuillez réessayer dans une heure.');
      } else {
        setResendError(err.response?.data?.detail || 'Une erreur est survenue.');
      }
    } finally {
      setResendLoading(false);
    }
  };

  // Loading state
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-teal-50 flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <Loader2 className="w-12 h-12 animate-spin text-[#1db584] mx-auto mb-4" />
            <p className="text-gray-600">Vérification de votre email en cours...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-teal-50 flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-10 h-10 text-[#1db584]" />
            </div>
            <CardTitle className="text-2xl text-gray-900">🎉 Compte Activé !</CardTitle>
            <CardDescription className="text-base mt-2">
              {message || 'Votre adresse email a été vérifiée avec succès.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800">
                <strong>✅ Votre compte est maintenant actif</strong>
                <p className="mt-1">Un email de bienvenue vous a été envoyé avec les détails de votre compte.</p>
                <p className="mt-2">Vous pouvez vous connecter et commencer à utiliser Baobab Connect.</p>
              </div>

              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-[#1db584] hover:bg-[#16a075] text-white"
              >
                Se connecter
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state (expired or invalid)
  if (status === 'expired' || status === 'error' || status === 'already-used') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-teal-50 flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            <CardTitle className="text-2xl text-gray-900">
              {status === 'expired' ? 'Lien expiré' : status === 'already-used' ? 'Lien déjà utilisé' : 'Lien invalide'}
            </CardTitle>
            <CardDescription className="text-base mt-2">
              {message}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {resendSuccess ? (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800">
                  <strong>✅ Email envoyé !</strong>
                  <p className="mt-1">
                    Un nouveau lien de vérification a été envoyé à <strong>{resendEmail}</strong>.
                    Vérifiez votre boîte de réception.
                  </p>
                </div>
                <Link to="/login">
                  <Button className="w-full bg-[#1db584] hover:bg-[#16a075] text-white">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Retour à la connexion
                  </Button>
                </Link>
              </div>
            ) : (
              <form onSubmit={handleResendVerification} className="space-y-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
                  <strong>📧 Besoin d'un nouveau lien ?</strong>
                  <p className="mt-1">Entrez votre email pour recevoir un nouveau lien de vérification.</p>
                </div>

                {resendError && (
                  <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                    {resendError}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email">Adresse email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="votre@email.com"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    required
                    className="focus:border-[#1db584] focus:ring-[#1db584]"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={resendLoading}
                  className="w-full bg-[#1db584] hover:bg-[#16a075] text-white"
                >
                  {resendLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Renvoyer le lien de vérification
                    </>
                  )}
                </Button>

                <div className="text-center pt-2">
                  <Link 
                    to="/login" 
                    className="text-sm text-[#1db584] hover:text-[#16a075] font-medium inline-flex items-center gap-1"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Retour à la connexion
                  </Link>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // No token state
  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-teal-50 flex items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <Mail className="w-10 h-10 text-blue-500" />
          </div>
          <CardTitle className="text-2xl text-gray-900">Vérification d'email</CardTitle>
          <CardDescription className="text-base mt-2">
            Entrez votre email pour recevoir un lien de vérification
          </CardDescription>
        </CardHeader>
        <CardContent>
          {resendSuccess ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800">
                <strong>✅ Email envoyé !</strong>
                <p className="mt-1">
                  Si un compte existe avec l'adresse <strong>{resendEmail}</strong>, 
                  vous recevrez un lien de vérification.
                </p>
              </div>
              <Link to="/login">
                <Button className="w-full bg-[#1db584] hover:bg-[#16a075] text-white">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Retour à la connexion
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleResendVerification} className="space-y-4">
              {resendError && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                  {resendError}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Adresse email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="votre@email.com"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  required
                  className="focus:border-[#1db584] focus:ring-[#1db584]"
                />
              </div>

              <Button
                type="submit"
                disabled={resendLoading}
                className="w-full bg-[#1db584] hover:bg-[#16a075] text-white"
              >
                {resendLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Envoyer le lien de vérification
                  </>
                )}
              </Button>

              <div className="text-center pt-4">
                <Link 
                  to="/login" 
                  className="text-sm text-[#1db584] hover:text-[#16a075] font-medium inline-flex items-center gap-1"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Retour à la connexion
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmail;
